import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { useAmplitude } from '@Analytics/hook'
import { SliderContext } from '@Components/Product/Slider/Context/SliderContext'
import { Product } from '@Generated/graphql'

const Slider: FC<{ children?: React.ReactNode, slides: NonNullable<Product['assets']> }> = ({ children, slides }) => {
  const [activeSlide, setActiveSlide] = useState({ key: 0, recalculate: true })
  const [isFullscreen, setFullscreen] = useState(false)
  const [isSwipedUp, setSwipeUp] = useState(false)
  const ampli = useAmplitude()

  useEffect(() => {
    const html = (document.getElementsByTagName('BODY') as HTMLCollectionOf<HTMLElement>)[0]
    html.style.overflow = isFullscreen ? 'hidden' : ''
    html.style.position = isFullscreen ? 'fixed' : ''
  }, [isFullscreen])

  const goToNextSlide = useCallback((recalculate: boolean) => {
    setActiveSlide((current) => {
      const next = current.key + 1
      const key = next >= slides.length ? 0 : next

      ampli.galleryInteracted({
        asset_position: key + 1,
        asset_type: slides[key]?.tags?.includes('video') ? 'video' : 'image',
        has_look: slides?.some(slide => slide?.tags?.includes('look')),
        has_video: slides?.some(slide => slide?.tags?.includes('video')),
        type: 'next',
      })

      return { key, recalculate }
    })
  }, [ampli, slides])

  const goToPreviousSlide = useCallback((recalculate: boolean) => {
    setActiveSlide((current) => {
      const next = current.key - 1
      const key = next < 0 ? slides.length - 1 : next

      ampli.galleryInteracted({
        asset_position: key + 1,
        asset_type: slides[key]?.tags?.includes('video') ? 'video' : 'image',
        has_look: slides?.some(slide => slide?.tags?.includes('look')),
        has_video: slides?.some(slide => slide?.tags?.includes('video')),
        type: 'previous',
      })

      return { key, recalculate }
    })
  }, [ampli, setActiveSlide, slides])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const toggleFullscreen = () => {
    ampli.galleryInteracted({
      asset_position: activeSlide.key + 1,
      asset_type: slides[activeSlide.key]?.tags?.includes('video') ? 'video' : 'image',
      has_look: slides?.some(slide => slide?.tags?.includes('look')),
      has_video: slides?.some(slide => slide?.tags?.includes('video')),
      type: 'fullscreen',
    })

    if (isFullscreen) {
      setActiveSlide({ key: activeSlide.key, recalculate: true })
    }
    setFullscreen(!isFullscreen)
  }
  const isVideoSlide = slides[activeSlide.key]?.tags?.includes('video') || false

  const value = useMemo(
    () => ({
      activeSlide,
      goToNextSlide,
      goToPreviousSlide,
      isFullscreen,
      isSwipedUp,
      isVideoSlide,
      setActiveSlide,
      setSwipeUp,
      slides,
      toggleFullscreen,
    }),
    [
      activeSlide,
      goToNextSlide,
      goToPreviousSlide,
      isFullscreen,
      isSwipedUp,
      isVideoSlide,
      slides,
      toggleFullscreen,
    ],
  )

  return (
    <SliderContext.Provider value={value}>
      {children}
    </SliderContext.Provider>
  )
}

export default Slider
